import { useCallback, useEffect, type CSSProperties } from 'react';
import { useNavigate, type To } from 'react-router-dom';

export type NavDirection = 'forward' | 'back';

const HERO_PARTS = ['avatar', 'name', 'amount'] as const;
const HERO_RELEASE_MS = 1500;
const BACK_CLAIM_WINDOW_MS = 1000;

export type HeroPart = (typeof HERO_PARTS)[number];

let heroId: string | null = null;
let heroElements: HTMLElement[] = [];
let releaseTimer: number | undefined;
let backMarkedAt = 0;

const isMobile = () => !window.matchMedia('(min-width: 768px)').matches;

export const heroName = (part: HeroPart) => `movement-${part}`;

export const heroStyle = (part: HeroPart, enabled: boolean): CSSProperties | undefined =>
	enabled ? { viewTransitionName: heroName(part) } : undefined;

const releaseHero = () => {
	heroElements.forEach((element) => (element.style.viewTransitionName = ''));
	heroElements = [];
	delete document.documentElement.dataset.hero;
};

const nameHero = (root: HTMLElement) => {
	window.clearTimeout(releaseTimer);
	releaseHero();
	heroElements = HERO_PARTS.flatMap((part) => {
		const element = root.querySelector<HTMLElement>(`[data-hero='${part}']`);
		if (!element) return [];
		element.style.viewTransitionName = heroName(part);
		return [element];
	});
	document.documentElement.dataset.hero = 'on';
	releaseTimer = window.setTimeout(releaseHero, HERO_RELEASE_MS);
};

export const markNavDirection = (direction: NavDirection) => {
	document.documentElement.dataset.nav = direction;
	if (direction === 'back') backMarkedAt = Date.now();
};

export const markHero = (root: HTMLElement | null, movementId: string) => {
	heroId = movementId;
	if (root && isMobile()) nameHero(root);
};

export const followHero = (movementId: string) => {
	heroId = movementId;
};

export const claimHero = (root: HTMLElement | null, movementId: string) => {
	if (!root || heroId !== movementId || Date.now() - backMarkedAt > BACK_CLAIM_WINDOW_MS || !isMobile()) return;
	heroId = null;
	nameHero(root);
};

export const useMarkBackOnPopState = () => {
	useEffect(() => {
		const markBack = () => markNavDirection('back');
		window.addEventListener('popstate', markBack);
		return () => window.removeEventListener('popstate', markBack);
	}, []);
};

export const useTransitionNavigate = () => {
	const navigate = useNavigate();
	return useCallback(
		(to: To, options?: { replace?: boolean }) => {
			markNavDirection('forward');
			void navigate(to, { ...options, viewTransition: true });
		},
		[navigate],
	);
};
